import type {
  ComputedRef,
  ExtractPropTypes,
  PropType,
  Ref,
} from 'vue';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import type { Dictionary } from 'lodash';
import type {
  ComponentDataSpec,
  ComponentMetadata,
  ComponentUXSpec,
  ErrorHandlingMode,
  FormInstance,
  PageTabSpec,
} from '../schemas';

export const userComponentProps = {
  modal: { type: Boolean, default: false },
  modalName: { type: String },
  componentInitErrorCallback: {
    type: Function as PropType<(error: unknown) => void>,
  },
  reload: { type: null },
};

export type UserComponentProps = ExtractPropTypes<typeof userComponentProps>;

export interface UserComponentOptions {
  metadata: ComponentMetadata;
  dataSpecs: ComponentDataSpec[];
  loaders: DataLoaderMap;
  ux: ComponentUXSpec;
  dataRefs?: Record<string, any>;
}

export interface SetDataOptions {
  noTouchLastLoaded?: boolean;
  noTakeSnapshot?: boolean;
}

export interface IUserComponent extends IAppHelpers, Omit<IComponentState, 'friendlyName'> {
  forms: Record<string, FormInstance | null>;
  registerForm: (name: string, form: FormInstance) => void;
  unregisterForm: (form: FormInstance) => void;
  loadData: (prop?: string | null, opts?: { initial?: boolean }) => Promise<void>;
  loadDataThrottled: (prop?: string | null, opts?: { initial?: boolean }) => Promise<void>;
  touchLastLoaded: () => void;
  handleError: (error: any, mode?: ErrorHandlingMode) => void;
  friendlyName: () => string;
  documentTitle: ComputedRef<string | undefined>;
  updateHeaders: () => void;
  setData: (path: string, value: any, opts?: SetDataOptions) => void;
}

export interface IComponentState {
  isUserComponent: boolean;
  $uid: Ref<string>;
  routeTab: Ref<PageTabSpec | null>;
  loading: Ref<boolean>;
  loaded: Ref<boolean>;
  reloading: Ref<boolean>;
  showLoadingIndicator: Ref<boolean>;
  error: Ref<any>;
  lastLoaded: Ref<number | null>;
  canLoadData: Ref<boolean>;
  pageDocument: Ref<PageTabSpec>;
  componentMetadata: Ref<ComponentMetadata>;
  ux: Ref<ComponentUXSpec>;
  isModalComponent: Ref<boolean>;
  modalName: Ref<string | undefined>;
  friendlyName: ComputedRef<string>;
}

export interface IAppHelpers {
  $app: ComputedRef<any>;
  $fn: ComputedRef<any>;
  $i18n: ComputedRef<any>;
  $store: ComputedRef<any>;
  $config: ComputedRef<any>;
  $imodal: ComputedRef<any>;
  $notification: ComputedRef<any>;
  $tabs: ComputedRef<any>;
  $filters: ComputedRef<any>;
  $route: RouteLocationNormalizedLoaded;
  $router: Router;
  gettext: (...args: any[]) => string;
  pgettext: (...args: any[]) => string;
  ngettext: (...args: any[]) => string;
  npgettext: (...args: any[]) => string;
  getSchema: (name: string) => any;
  searchDataSource: (operation: string, parameters?: Dictionary<any>) => any;
  randomElementId: () => string;
  schemaDefaults: (name: string, extras?: object) => any;
  datetime: (value: any, opts?: any) => any;
  date: (value: any, opts?: any) => any;
  time: (value: any, opts?: any) => any;
  number: (value: any, opts?: any) => any;
  money: (value: any, currency?: string, digits?: number) => any;
  distance: (value: any) => any;
  username: (value: any) => any;
  filesize: (value: any) => any;
}

export interface DataLoaderMap {
  [key: string]: () => unknown;
}

export interface UseDataLoaderOptions {
  state: any;
  friendlyName: () => string;
  dataSpecs: ComponentDataSpec[];
  loaders: DataLoaderMap;
  context: any;
  ux?: any;
  helpers: any;
  onReloadStart?: () => (() => void) | undefined;
}

export interface ComponentScopeExtras {
  emit: (event: string, ...args: any[]) => void;
  forms: Record<string, FormInstance | null>;
  error: Ref<any>;
  isModalComponent: Ref<boolean>;
  modalName: Ref<string | undefined>;
  componentMetadata: ComponentMetadata;
  loadData: (
    prop?: string | null,
    opts?: { initial?: boolean; errorHandling?: ErrorHandlingMode },
  ) => Promise<void>;
  registerForm: (name: string, form: FormInstance) => void;
  unregisterForm: (form: FormInstance) => void;
  setData: (path: string, value: any, opts?: SetDataOptions) => void;
  touchLastLoaded: () => void;
  handleError: (error: any, mode?: ErrorHandlingMode) => void;
}

export type PageLoadState = 'loading' | 'loaded' | 'failed';

export interface LoadReporter {
  reportLoaded(): void;
  reportFailed(error: unknown): void;
  stopWaiting(): void;
}

export interface PageLoadTracker {
  state: Readonly<Ref<PageLoadState>>;
  error: Readonly<Ref<unknown>>;
  retryCount: Readonly<Ref<number>>;
  reloading: Readonly<Ref<boolean>>;
  pageReporter: LoadReporter;
  waitFor(componentName: string): LoadReporter;
  reloadStarted(): () => void;
  whenFinished(): Promise<void>;
  retry(): void;
  stillLoading(): string[];
  stop(): void;
}

export interface PageLoadTrackerOptions {
  name?: string;
  timeoutMs?: number;
}
